<script setup>

import { defineAsyncComponent } from 'vue'

const HistoryUpcomingList = defineAsyncComponent(() => 
  import('./historyUpcomingList.vue')
)

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section :id="props.block?.['anchorId']"
  class="history-and-upcoming section-mb">
  <div class="container">
    <div class="history-and-upcoming__head">
      <div v-if="props.block?.['headingAdvanced']?.html"
        v-html="props.block?.['headingAdvanced']?.html"
        class="history-and-upcoming__heading"></div>
      <p v-if="props.block?.['description']"
        class="history-and-upcoming__description">
        {{ props.block?.['description'] }}
      </p>
    </div>

    <div v-if="props.block?.['cards']?.length || props.block?.['historyUpcomingMatrix']?.length"
      class="history-and-upcoming__content-wrapper">
      <ul v-if="props.block?.['cards']?.length"
        class="history-and-upcoming__content"
        :class="{ '--center-text-with-circle': props.block?.['centerTextWithCircle'] }"
      >
        <li v-for="(card, index) in props.block?.['cards']"
          :key="card.id"
          class="history-and-upcoming__item">
          <div class="history-and-upcoming__item-index">
            <span class="history-and-upcoming__item-index-number">{{ index + 1 }}</span>
            <span class="history-and-upcoming__item-index-divider"></span>
          </div>
          <div class="history-and-upcoming__item-content">
            <p v-if="card.title"
              class="history-and-upcoming__item-title">{{ card.title }}</p>
            <p v-if="card.description"
              class="history-and-upcoming__item-description">{{ card.description }}</p>
            <p v-if="card.badge"
              class="history-and-upcoming__item-badge">{{ card.badge }}</p>
          </div>
        </li>
      </ul>

      <HistoryUpcomingList
        v-if="props.block?.['historyUpcomingMatrix']?.length"
        :items="props.block['historyUpcomingMatrix']"
      />
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.history-and-upcoming__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  margin-bottom: 32px;

  @media (min-width: 1024px) {
    gap: 24px;
    margin-bottom: 48px;
  }
}

.history-and-upcoming__heading {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.history-and-upcoming__description {
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-gray-500);

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 140%; /* 28px */
    letter-spacing: -0.4px;
  }
}


.history-and-upcoming__heading :deep(h2) {
  font-size: 28px;
  font-weight: 500;
  line-height: 120%; /* 33.6px */
  letter-spacing: -0.56px;
  color: var(--color-gray-800);

  @media (min-width: 1024px) {
    font-size: 38px;
    line-height: 120%; /* 45.6px */
    letter-spacing: -0.76px;
  }
}

.history-and-upcoming__content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 48px;

  @media (min-width: 1024px) {
    gap: 32px;
  }
}

.history-and-upcoming__content {
  display: flex;
  flex-direction: column;
  padding: 68px 54px 68px 50px;
  border-radius: 16px;
  background: var(--color-gray-100);

  @media (min-width: 1024px) {
    padding: 54px 24px;
    flex-direction: row;
  }

  @media (min-width: 1440px) {
    padding: 79px 82px;
  }
}

.history-and-upcoming__item {
  display: flex;
  gap: 13px;

  @media (min-width: 1024px) {
    flex: 1;
    flex-direction: column;
    gap: 9px;

    &:last-child {
      flex: calc(236 / 286);
    }
  }
}

.history-and-upcoming__item-index {
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
}

.history-and-upcoming__item-index-number {
  display: flex;
  width: 27.074px;
  height: 27.074px;
  justify-content: center;
  align-items: center;
  color: var(--color-white);
  background: var(--color-black);
  border-radius: 50%;
  font-size: 12.317px;
  font-weight: 600;
  line-height: 10.153px;
  padding-bottom: 2px;

  @media (min-width: 1024px) {
    width: 36px;
    height: 36px;
    font-size: 15px;
    line-height: 13.5px;
    flex-shrink: 0;
  }
}

.history-and-upcoming__item-index-divider {
  width: 1.658px;
  min-height: 119px;
  background: var(--color-gray-250);

  @media (min-width: 1024px) {
    width: 100%;
    height: 1.5px;
    min-height: unset;
    background: var(--color-gray-200);
  }
}

.history-and-upcoming__item:last-child .history-and-upcoming__item-index-divider {
  display: none;

  @media (min-width: 1024px) {
    display: block;
    background: linear-gradient(90deg, var(--color-gray-200) 0%, var(--color-gray-100) 100%);
    max-width: 70%;
  }
}

.history-and-upcoming__item-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.history-and-upcoming__item-title {
  font-size: 16.545px;
  font-weight: 500;
  line-height: 144%; /* 23.825px */
  letter-spacing: -0.331px;
  color: var(--color-gray-800);
  margin-bottom: 12px;

  @media (min-width: 1024px) {
    font-size: 22px;
    line-height: 144%; /* 31.68px */
    letter-spacing: -0.44px;
    margin-bottom: 16px;
  }
}

.history-and-upcoming__item-description {
  font-size: 12.033px;
  line-height: 140%; /* 16.846px */
  letter-spacing: -0.241px;
  color: var(--color-gray-350);

  @media (min-width: 1024px) {
    width: calc(236 / 286 * 100%);
    max-width: 235px;
    font-size: 16px;
    line-height: 140%; /* 22.4px */
    letter-spacing: -0.32px;
  }
}

.history-and-upcoming__item:first-child .history-and-upcoming__item-description {
  @media (min-width: 1024px) {
    max-width: 248px;
    width: calc(249 / 286 * 100%);
  }
}

.history-and-upcoming__item:last-child .history-and-upcoming__item-description {
  @media (min-width: 1024px) {
    max-width: 100%;
    width: 100%;
  }
}

.history-and-upcoming__item-badge {
  border-radius: 5.264px;
  background: #D0FA03;
  padding: 1.75px 3.858px 2px 3.858px;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: var(--color-gray-700);
  margin-top: 12px;

  @media (min-width: 1024px) {
    font-size: 12px;
    line-height: 18px;
    padding: 2px 5.129px 2.5px 5.129px;
    border-radius: 7px;
    margin-top: 16px;
  }
}

.--center-text-with-circle {

  .history-and-upcoming__item-index-divider {
    min-height: 99px;
  }

  .history-and-upcoming__item-description {
    max-width: 176px;
  }

  @media (min-width: 1024px) {

    .history-and-upcoming__item {
      gap: 20px;
    }

    .history-and-upcoming__item-index {
      width: 100%;
    }

    .history-and-upcoming__item-index::before {
      content: '';
      flex: 1 1 0;
      min-width: 0;
      height: 1.5px;
      background: var(--color-gray-200);
    }

    .history-and-upcoming__item-index-divider {
      flex: 1 1 0;
      min-width: 0;
      max-width: none;
      min-height: unset;
      background: var(--color-gray-200);
    }

    .history-and-upcoming__item:first-child .history-and-upcoming__item-index::before {
      background: transparent;
    }

    .history-and-upcoming__item:last-child .history-and-upcoming__item-index-divider {
      display: block;
      max-width: none;
      background: transparent;
    }

    .history-and-upcoming__item-content {
      align-items: center;
    }

    .history-and-upcoming__item-title,
    .history-and-upcoming__item-description {
      text-align: center;
    }
  }
}

@media (min-width: 1440px) {
    .--center-text-with-circle.history-and-upcoming__content {
      padding: 87px 92px 77px 95px;
    }
  }

</style>